import { motion } from 'framer-motion'
import type { Article } from '../../data/articles'

type Props = {
  cover: Article['cover']
  hovered?: boolean
  className?: string
}

export default function ArticleCover({ cover, hovered = false, className = '' }: Props) {
  const baseBg =
    cover.palette === 'accent'
      ? 'bg-gradient-to-br from-accent-500 via-accent-600 to-accent-800'
      : cover.palette === 'ink'
      ? 'bg-gradient-to-br from-ink-900 via-ink-950 to-ink-900'
      : 'bg-gradient-to-br from-ink-950 via-accent-800 to-accent-600'

  return (
    <div className={`relative overflow-hidden ${baseBg} ${className}`}>
      {/* Pattern */}
      {cover.pattern === 'grid' && <GridPattern hovered={hovered} />}
      {cover.pattern === 'dots' && <DotsPattern hovered={hovered} />}
      {cover.pattern === 'beam' && <BeamPattern hovered={hovered} />}
      {cover.pattern === 'waves' && <WavesPattern hovered={hovered} />}
      {cover.pattern === 'package' && <PackagePattern hovered={hovered} />}

      {/* Glow overlay on hover */}
      <motion.div
        aria-hidden
        initial={false}
        animate={{ opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.4 }}
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.25), transparent 60%)',
        }}
      />

      {/* Subtle vignette */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.35) 100%)',
        }}
      />
    </div>
  )
}

function GridPattern({ hovered }: { hovered: boolean }) {
  return (
    <motion.div
      aria-hidden
      animate={{ scale: hovered ? 1.1 : 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0"
    >
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
    </motion.div>
  )
}

function DotsPattern({ hovered }: { hovered: boolean }) {
  return (
    <motion.div
      aria-hidden
      animate={{ scale: hovered ? 1.08 : 1, rotate: hovered ? 2 : 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 opacity-40"
      style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.5) 1.5px, transparent 1.5px)',
        backgroundSize: '22px 22px',
      }}
    />
  )
}

function BeamPattern({ hovered }: { hovered: boolean }) {
  return (
    <div aria-hidden className="absolute inset-0">
      <motion.div
        animate={{ x: hovered ? '8%' : '0%' }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
      >
        <svg viewBox="0 0 400 300" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="beam1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(255,255,255,0)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.7)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.path
              key={i}
              d={`M 0 ${40 + i * 55} Q 200 ${80 + i * 30} 400 ${60 + i * 40}`}
              stroke="url(#beam1)"
              strokeWidth={hovered ? 2 : 1.2}
              fill="none"
              animate={{ pathLength: [0, 1, 1], opacity: [0, 0.9, 0] }}
              transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
            />
          ))}
        </svg>
      </motion.div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.2),transparent_70%)]" />
    </div>
  )
}

function WavesPattern({ hovered }: { hovered: boolean }) {
  return (
    <motion.svg
      aria-hidden
      viewBox="0 0 400 300"
      className="absolute inset-0 w-full h-full opacity-50"
      preserveAspectRatio="none"
      animate={{ y: hovered ? -6 : 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      {[0, 1, 2].map((i) => (
        <motion.path
          key={i}
          d={`M 0 ${120 + i * 40} Q 100 ${80 + i * 40} 200 ${120 + i * 40} T 400 ${120 + i * 40}`}
          stroke="rgba(255,255,255,0.5)"
          strokeWidth={1.4}
          fill="none"
          animate={{ d: [
            `M 0 ${120 + i * 40} Q 100 ${80 + i * 40} 200 ${120 + i * 40} T 400 ${120 + i * 40}`,
            `M 0 ${120 + i * 40} Q 100 ${160 + i * 40} 200 ${120 + i * 40} T 400 ${120 + i * 40}`,
            `M 0 ${120 + i * 40} Q 100 ${80 + i * 40} 200 ${120 + i * 40} T 400 ${120 + i * 40}`,
          ] }}
          transition={{ duration: 5 + i, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </motion.svg>
  )
}

function PackagePattern({ hovered }: { hovered: boolean }) {
  return (
    <motion.div
      aria-hidden
      animate={{ rotate: hovered ? -4 : 0, scale: hovered ? 1.05 : 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 flex items-center justify-center"
    >
      <svg viewBox="0 0 200 200" className="w-3/4 h-3/4 opacity-80">
        <defs>
          <linearGradient id="pkg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.55)" />
          </linearGradient>
        </defs>
        <path
          d="M40 60 L100 30 L160 60 L160 140 L100 170 L40 140 Z"
          fill="url(#pkg)"
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="1.5"
        />
        <path d="M40 60 L100 90 L160 60" fill="none" stroke="rgba(0,0,0,0.2)" strokeWidth="1.2" />
        <path d="M100 90 L100 170" fill="none" stroke="rgba(0,0,0,0.2)" strokeWidth="1.2" />
        <rect x="75" y="70" width="50" height="14" fill="#0e0e14" rx="2" />
      </svg>
    </motion.div>
  )
}
